// src/components/Sidebar/MobileTabBar.jsx
//
// La barra de abajo. Lleva **todos** los destinos y se desplaza en horizontal,
// en vez de quedarse con cinco y mandar el resto al cajón.
//
// La alternativa era elegir cinco, y elegir cinco de nueve es elegir mal para
// alguien: en las maquetas la quinta plaza se la disputaban Data y Zeno, que es
// justo la señal de que no hay un corte bueno. Deslizándose, cada destino
// conserva su tamaño —el pulgar necesita esos 76px— y ninguno queda escondido
// detrás de una decisión nuestra.
//
// Lo que hay que cuidar cuando una barra se desplaza es que no se pierda de
// vista lo que importa, y de eso se ocupan dos cosas:
//
//   1. El destino activo se trae a la vista solo. Sin esto, entrar en
//      Connections desde el cajón dejaba la barra mostrando Home..Shop y ningún
//      indicio de dónde estás.
//   2. Un degradado en el borde por el que queda barra. Es la única pista de
//      que hay más: sin ella, una fila que termina justo en el borde se lee
//      como una fila completa.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RAIL_DESTINATIONS } from "../../lib/navigation";
import { cn } from "../../lib/utils";

export function MobileTabBar({ active, onSelect }) {
  const { t } = useTranslation();
  const scrollerRef = useRef(null);
  const activeRef = useRef(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  const syncEdges = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setEdges({ start: el.scrollLeft > 4, end: el.scrollLeft < max - 4 });
  }, []);

  // Centrar el activo. Se mueve `scrollLeft` a mano en vez de usar
  // `scrollIntoView`: ese sube por los ancestros y acaba desplazando también la
  // página, que es justo lo que no queremos desde una barra fija.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const item = activeRef.current;
    if (!scroller || !item) return;
    const target =
      item.offsetLeft - scroller.clientWidth / 2 + item.offsetWidth / 2;
    const max = scroller.scrollWidth - scroller.clientWidth;
    scroller.scrollTo({
      left: Math.max(0, Math.min(target, max)),
      behavior: "smooth",
    });
  }, [active]);

  useEffect(() => {
    syncEdges();
    const el = scrollerRef.current;
    if (!el) return undefined;
    // Escuchamos el scroll y el cambio de tamaño; nada de temporizadores.
    el.addEventListener("scroll", syncEdges, { passive: true });
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(syncEdges) : null;
    observer?.observe(el);
    return () => {
      el.removeEventListener("scroll", syncEdges);
      observer?.disconnect();
    };
  }, [syncEdges]);

  return (
    <nav
      aria-label={t("sidebar.navLabel", "Main")}
      className={cn(
        "fixed inset-x-0 bottom-0 z-[55] md:hidden",
        "border-t border-white/10 bg-[#08040F]/95 backdrop-blur-xl",
        // El iPhone dibuja su barra de gestos encima de lo que haya: sin esto
        // la fila de iconos queda justo debajo de ella.
        "pb-[env(safe-area-inset-bottom)]"
      )}
    >
      <div className="relative">
        <ul
          ref={scrollerRef}
          className="flex items-stretch overflow-x-auto overscroll-x-contain [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {RAIL_DESTINATIONS.map((entry) => {
            const isActive = entry.id === active;
            const { Icon } = entry;
            return (
              <li key={entry.id} className="shrink-0">
                <button
                  type="button"
                  ref={isActive ? activeRef : undefined}
                  onClick={() => onSelect(entry.id)}
                  aria-current={isActive ? "page" : undefined}
                  className={cn(
                    "flex h-16 w-[76px] flex-col items-center justify-center gap-1 px-1",
                    "transition-colors focus-visible:outline-none",
                    "focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-purple-400/70",
                    isActive ? "text-violet-300" : "text-white/55 active:text-white/80"
                  )}
                >
                  <Icon
                    className="h-[22px] w-[22px] shrink-0"
                    strokeWidth={isActive ? 2.2 : 1.8}
                  />
                  <span
                    className={cn(
                      "max-w-full truncate text-[11px] leading-none",
                      isActive ? "font-semibold" : "font-medium"
                    )}
                  >
                    {t(entry.i18nKey, entry.label)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        {edges.start && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-[#08040F] to-transparent"
          />
        )}
        {edges.end && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-[#08040F] to-transparent"
          />
        )}
      </div>
    </nav>
  );
}

export default MobileTabBar;
